import ngTranslate from 'angular-translate';
import get from 'lodash/get';

import { CreditCardServiceModule } from 'services/payment/stripe/creditCard.service';
import { PaymentMethodServiceModule } from 'services/payment/paymentMethod/paymentMethod.service';
import { ExpireCacheServiceModule } from 'services/utilities/expireCache.service';
import { DataServiceModule } from 'services/data/data.service';

export const isDowngrade = (currentPlan, newPlan) => getDailyPriceOfPlan(currentPlan) > getDailyPriceOfPlan(newPlan);
const getDailyPriceOfPlan = plan => plan.amount / getNumberOfDaysInPlanCycle(plan);
const getNumberOfDaysInPlanCycle = (plan) => {
    const daysInIntervals = { day: 1, week: 7, month: 30, year: 365 };
    return daysInIntervals[plan.interval] * plan.interval_count;
};

export const SubscriptionServiceModule = angular
    .module('app.services.access.subscription', [
        ngTranslate,
        CreditCardServiceModule,
        PaymentMethodServiceModule,
        ExpireCacheServiceModule,
        DataServiceModule
    ])
    .service('subscriptionsService', (
        $http,
        $q,
        $cacheFactory,
        creditCardService,
        $translate,
        $rootScope,
        paymentMethodService,
        dataService,
        $window,
        expireCache
    ) => {
        let cache = $cacheFactory('subscriptionCache');
        let subscriptionsUrl = `${process.env.BASE_API_URL}/admin/subscriptions`;

        let service = {
            cache,
            changePlan,
            getPlan,
            getPlanFeatures,
            getPlanName,
            getSubscription,
            getTaxPercent,
            getGrandTotal,
            subscribeToPlusPlan,
            unsubscribeFromPlusPlan,
            updateSubscription,
            appPlans: {
                'Personal-1App': { maxInstalledApps: 1 },
                'Professional-3Apps': { maxInstalledApps: 3 },
                'Business-5Apps': { maxInstalledApps: 5 },
                '3M-Personal-1App': { maxInstalledApps: 1 },
                '3M-Professional-3Apps': { maxInstalledApps: 3 },
                '3M-Business-5Apps': { maxInstalledApps: 5 }
            }
        };

        function unsubscribeFromPlusPlan() {
            return service.changePlan(process.env.LITE_PLAN_ID);
        }

        function subscribeToPlusPlan(user, card, billingAddress) {
            return creditCardService.createStripeCcToken(card, user, billingAddress).then((cardResponse) => {
                const stripeCcToken = get(cardResponse, 'id', null);
                return service.changePlan(process.env.PLUS_PLAN_ID, stripeCcToken);
            }, (error) => {
                return $q.reject({
                    description: 'Stripe Token Error',
                    data: error
                });
            });
        }

        function changePlan({ stripeToken, planId, couponCode, trialDaysRemaining, address }) {
            return updatePaymentMethod(stripeToken)
                .catch((error) => {
                    return $q.reject({
                        message: 'There was a problem with your payment card. Please check your payment details and try again.', // eslint-disable-line max-len
                        data: error
                    });
                })
                .then(service.getSubscription)
                .then((subscription) => {
                    let failureMessage = 'Failed to change plan';
                    if (subscription.plan) {
                        subscription.plan.id = planId;
                    } else {
                        return $q.reject({
                            description: failureMessage
                        });
                    }

                    if (couponCode) {
                        subscription.couponCode = couponCode;
                    }

                    if (trialDaysRemaining) {
                        subscription.trialLengthInDays = trialDaysRemaining;
                    }

                    if (address) {
                        subscription.address = address;
                    }

                    return service.updateSubscription(subscription)
                        .then((response) => {
                            $rootScope.$emit('SubscriptionService:PlanChanged', response);
                            return response;
                        })
                        .catch((error) => {
                            return $q.reject({
                                description: failureMessage,
                                data: error
                            });
                        })
                        .then((subscriptionResponse) => {
                            expireCache();
                            return subscriptionResponse;
                        });
                });
        }

        function updatePaymentMethod(stripeToken) {
            if (!stripeToken) return $q.resolve();
            return paymentMethodService.updatePaymentMethod(stripeToken).catch((error) => {
                return $q.reject({
                    description: 'Failed to update payment information',
                    data: error
                });
            });
        }

        function getPlanName() {
            return service.getSubscription().then(response => response.plan.name);
        }

        function getPlan() {
            return service.getSubscription().then(response => response.plan);
        }

        function getTaxPercent(state, zipcode) {
            return $http.get(`${process.env.BASE_API_URL}/admin/taxrate`, {
                params: {
                    state,
                    zipcode
                }
            }).then(response => response.data.taxPercent);
        }

        function getGrandTotal(plan, state, zip) {
            return $http.post(`${process.env.BASE_API_URL}/admin/subscriptions/grandtotal`, {
                plan,
                address: {
                    state,
                    zip
                }
            }).then(grandTotalResponse => grandTotalResponse.data);
        }

        function getSubscription() {
            return $http.get(subscriptionsUrl, { cache: service.cache })
                .then(response => response.data);
        }

        function updateSubscription(subscription) {
            return $http.put(subscriptionsUrl, subscription).then((response) => {
                service.cache.removeAll();
                service.cache.put('subscriptionCache', response.data);

                return response.data;
            });
        }

        function getPlanFeatures(planName) {
            let translationKey = planName === 'plus' ? 'MYACCOUNT.PLUSFEATURES' : '';
            return $translate([translationKey]).then((response) => {
                let features = response[translationKey];
                return features ? features.split('_') : [];
            });
        }

        return service;
    })
    .name;



// WEBPACK FOOTER //
// ./src/app/services/access/subscription/subscription.service.js